import { getPlanDetails } from "../store/userSlice";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

function HighAlertIcon() {
  return (
    <div className="relative w-8 h-8 shrink-0 flex items-center justify-center">
      {/* Pulsing ring */}
      <span className="absolute inset-0 rounded-lg bg-red-400 opacity-30 animate-ping" />
      {/* Icon bg */}
      <div className="relative w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc2626"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Shield */}
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          {/* X inside shield */}
          <line x1="9" y1="9" x2="15" y2="15" />
          <line x1="15" y1="9" x2="9" y2="15" />
        </svg>
      </div>
    </div>
  );
}
function SidebarWarningStrip({ type, overLimitSince, isOpen, onToggle }) {
  const plan = useSelector(getPlanDetails);
  const diff =
    type === "users"
      ? plan.currentUsers - plan.maxUsers
      : plan.currentWorkflows - plan.maxWorkflows;

  const getGracePeriodRemaining = (overLimitSince) => {
    if (!overLimitSince) return 7;

    const GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const startDate = new Date(overLimitSince).getTime();
    const expiryDate = startDate + GRACE_PERIOD_MS;
    const now = new Date().getTime();

    const remainingMs = expiryDate - now;

    // Convert MS to Days and round up so "0.5 days" shows as "1 day"
    const daysLeft = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    return Math.max(0, daysLeft); // Ensure we never return a negative number
  };

  return (
    <div className="px-3 pb-2">
      {/* Trigger Button */}
      <button
        onClick={() => onToggle()}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 border-[1.5px] border-red-200 cursor-pointer text-left transition-all duration-150
          bg-gradient-to-br from-red-50 to-white
          hover:border-red-400 hover:shadow-[0_2px_12px_rgba(239,68,68,0.12)]
          ${isOpen ? "rounded-t-xl rounded-b-none border-b-0" : "rounded-xl"}`}
      >
        <HighAlertIcon />
        {/* Label */}
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold  text-red-600  leading-tight`}>
            <span className="uppercase">{type}</span> limit exceeded
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5 font-bold">
            Tap to see details
          </div>
        </div>

        {/* Chevron */}
        <span
          className={`shrink-0 text-slate-400 text-[9px] transition-transform duration-200 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          ▼
        </span>
      </button>

      {/* Expanded Panel */}
      {isOpen && (
        <div className="border-[1.5px] border-t-0 border-red-200 rounded-b-xl px-3 pt-3 pb-3 bg-white shadow-[0_4px_16px_rgba(239,68,68,0.08)]">
          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-2 mb-1">
            <div className="bg-slate-50 rounded-lg px-2.5 py-2">
              <div className="text-[9px] font-semibold text-slate-400 uppercase  tracking-widest">
                Plan
              </div>
              <div className="text-sm font-bold text-slate-800 mt-0.5 uppercase">
                {plan.plan}
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg px-2.5 py-2">
              <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest">
                {type === "users" ? "Users" : "Workflows"}
              </div>
              <div className="text-sm font-bold text-red-500 mt-0.5">
                {type === "users" ? (
                  <>
                    {plan.currentUsers}/{plan.maxUsers}
                  </>
                ) : (
                  <>
                    {plan.currentWorkflows}/{plan.maxWorkflows}
                  </>
                )}
              </div>
            </div>
          </div>

          <p className="text-black text-[13px] mb-3.5 leading-relaxed p-1">
            You are{" "}
            <strong className="text-red-600 font-bold">
              {diff} {type === "users" ? "users" : "workflows"} over
            </strong>{" "}
            your limit. Please remove extra{" "}
            {type === "users" ? "users" : "workflows"} or the system will
            automatically remove them in
            <span className="text-red-600 font-bold">
              {" "}
              {getGracePeriodRemaining(overLimitSince)} days
            </span>
            .
          </p>

          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-slate-100 my-3">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-orange-400 to-red-500 transition-all duration-500"
              style={{ width: `${100}%` }}
            />
          </div>

          <div className="flex flex-col gap-2 w-full mt-2">
            {/* Primary Action: Fix the current problem */}
            <Link
              className="flex items-center justify-center w-full h-[38px] rounded-lg bg-slate-900 text-white font-bold text-[12px] transition-all hover:bg-black hover:shadow-lg"
              to={type === "users" ? "/portal/users" : "/portal/workflows"}
              onClick={() => onToggle()}
            >
              {type === "users" ? "Manage Users" : "Manage Workflows"}
            </Link>

            {/* Secondary Action: The alternative fix */}
            <Link
              className="flex items-center justify-center w-full h-[38px] rounded-lg bg-white border border-slate-200 text-slate-600 font-semibold text-[12px] transition-all hover:border-slate-400 hover:text-slate-800"
              to="/portal/account"
              onClick={() => onToggle()}
            >
              Upgrade Plan
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default SidebarWarningStrip;
